import { useMemo } from 'react';
import { Card, Stack, Typography } from '@locus-taxy/encore-mui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';
import { ProfileViewRenderer } from './ProfileViewRenderer';

interface WorkProfileSectionProps {
  roleName: string;
  teamNames: string;
  skillNames: string;
  joiningDate: { local: string; timeZone: string } | null;
  locale?: string;
}

const displayDate = (
  temporal: { local: string; timeZone: string } | null | undefined,
  locale?: string
): string | null => {
  const localDate = temporal?.local;
  if (!localDate) { return null; }
  try {
    const [y, m, d] = localDate.split('-').map(Number);
    return new Intl.DateTimeFormat(locale ?? 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(y, m - 1, d));
  } catch {
    return localDate;
  }
};

export function WorkProfileSection({
  roleName, teamNames, skillNames, joiningDate, locale,
}: Readonly<WorkProfileSectionProps>) {
  const { t } = useTranslation();

  const fields = useMemo(() => [
    { label: t('userDetails.role', 'Role'), value: roleName },
    { label: t('userDetails.teams', 'Teams'), value: teamNames },
    { label: t('userDetails.skills', 'Skills'), value: skillNames },
    { label: t('userDetails.joiningDate', 'Joining Date'), value: displayDate(joiningDate, locale) },
  ], [t, roleName, teamNames, skillNames, joiningDate, locale]);

  return (
    <Card
      elevation={0}
      variant="outlined"
      data-testid={getTestId({ action: TestIdAction.VIEW, object: 'work-profile-section', page: 'account' })}
    >
      <Stack spacing={3} padding={4}>
        <Stack spacing={0.5}>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {t('userDetails.workProfile', 'Work Profile')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('userDetails.workProfileDescription', 'These details are managed by your administrator.')}
          </Typography>
        </Stack>

        <ProfileViewRenderer fields={fields} columns={2} />
      </Stack>
    </Card>
  );
}
